import React, { FC, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, List, ListItem, ListItemText } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import BatchSkippedOrders from 'components/orders/shared/batch-skipped-orders.component';
import {
    batchMarkOrdersProductionFinishedTemplate,
    batchProductionFinishedPreviewTemplate,
} from 'apollo/templates/order';
import { BatchOrdersResult, BatchProductionFinishedPreview } from 'models/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type BatchProductionFinishedDialogProps = {
    open: boolean;
    orderIds: string[];
    onClose: () => void;
    onDone: (result: BatchOrdersResult) => void;
};

/**
 * DD-3463: marks the selected orders production finished. No questions, only a
 * confirm step showing which orders will be finished and which are skipped.
 */
const BatchProductionFinishedDialog: FC<BatchProductionFinishedDialogProps> = ({
    open,
    orderIds,
    onClose,
    onDone,
}: BatchProductionFinishedDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    // Prefilled: one PDF with every delivery note of the batch. The user can opt out.
    const [compileDeliveryNotes, setCompileDeliveryNotes] = useState(true);

    const { data, loading } = useQuery<{ batchProductionFinishedPreview: BatchProductionFinishedPreview }>(
        batchProductionFinishedPreviewTemplate,
        {
            variables: { orderIds },
            fetchPolicy: 'network-only',
            skip: !open,
            onError(error) {
                showBoundary(error);
            },
        },
    );
    const preview = data?.batchProductionFinishedPreview;

    const [batchMarkOrdersProductionFinished, { loading: running }] = useMutation<{
        batchMarkOrdersProductionFinished: BatchOrdersResult;
    }>(batchMarkOrdersProductionFinishedTemplate, {
        onCompleted: ({ batchMarkOrdersProductionFinished: result }) => {
            onDone(result);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleConfirm = () => {
        batchMarkOrdersProductionFinished({
            variables: { orderIds: preview?.eligible.map((order) => order._id) ?? [], compileDeliveryNotes },
        });
    };

    const eligibleCount = preview?.eligible.length ?? 0;

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={translate('orders:BATCH_PRODUCTION_FINISHED')}
            actions={
                <>
                    <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                    {eligibleCount ? (
                        <LoadingButton
                            variant="contained"
                            onClick={handleConfirm}
                            loading={running}
                            data-test="batch-production-finished-dialog.confirm"
                        >
                            {translate('orders:BATCH_PRODUCTION_FINISHED_BUTTON', { count: eligibleCount })}
                        </LoadingButton>
                    ) : undefined}
                </>
            }
        >
            {loading || !preview ? (
                <Loader />
            ) : (
                <Box>
                    {eligibleCount ? (
                        <>
                            <Alert severity="info">{translate('orders:BATCH_PRODUCTION_FINISHED_INFO')}</Alert>
                            <List dense>
                                {preview.eligible.map((order) => (
                                    <ListItem key={order._id}>
                                        <ListItemText primary={order.orderId} />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    ) : (
                        <Alert severity="info">{translate('orders:BATCH_NO_ELIGIBLE_ORDERS')}</Alert>
                    )}
                    <BatchSkippedOrders skipped={preview.skipped} />
                    {eligibleCount ? (
                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={compileDeliveryNotes}
                                    onChange={(event) => setCompileDeliveryNotes(event.target.checked)}
                                    data-test="batch-production-finished-dialog.compile-pdf-checkbox"
                                />
                            }
                            label={translate('orders:CREATE_COMPILED_DELIVERY_NOTES_PDF')}
                        />
                    ) : undefined}
                </Box>
            )}
        </DHDialog>
    );
};

export default BatchProductionFinishedDialog;
